import React, { createContext, useContext, useState, useEffect } from "react";
import { authService } from "../utils/authService";
import api from "../utils/api";

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Monitor auth state
  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      setCurrentUser(user);
      setLoading(true);

      if (user) {
        try {
          console.log("[AuthContext] Firebase user object:", user);

          // Force refresh the ID token to get the latest custom claims
          await user.getIdToken(true);
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;

          // Store the auth token for real-time notifications
          setAuthToken(tokenResult.token);

          console.log(
            "[AuthContext] Firebase Auth claims (force-refreshed):",
            claims
          );

          // Get additional user profile data from backend
          const { data: profile } = await api.get("/users/profile");
          console.log("[AuthContext] Server profile fetched:", profile);
          setUserProfile(profile);

          // Attach the role and department info to the user object for access throughout the app
          if (profile) {
            // Prioritize claims over Firestore profile for role and department
            user.role = claims.role || profile.role;
            user.departmentId = claims.departmentId || profile.departmentId;
            user.department = claims.department || profile.department;

            console.log("[AuthContext] User authenticated:", user);
            console.log("[AuthContext] User profile loaded:", profile);
            console.log("[AuthContext] Assigned role:", user.role);
            console.log("[AuthContext] Department data from claims:", {
              department: claims.department,
              departmentId: claims.departmentId,
            });
            console.log("[AuthContext] Final department data on user object:", {
              department: user.department,
              departmentId: user.departmentId,
            });
            // Mark if the user must change password on first login
            user.requirePasswordChange = profile.passwordChanged === false;
          } else {
            // Even without profile, we can still use claims if available
            if (claims.role) user.role = claims.role;
            if (claims.department) user.department = claims.department;
            if (claims.departmentId) user.departmentId = claims.departmentId;

            console.warn(
              "[AuthContext] No profile found for user UID, using claims only:",
              user.uid
            );
          }
        } catch (err) {
          console.error("[AuthContext] Error fetching user data:", err);
          setError(err.message);
        }
      } else {
        console.warn("[AuthContext] No Firebase user detected (user is null)");
        setUserProfile(null);
        setAuthToken(null);
      }

      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, []);

  // Login
  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);

      // Check if credentials is an object with universityId/password or direct email/password parameters
      if (
        credentials &&
        typeof credentials === "object" &&
        credentials.universityId
      ) {
        // For login form that uses universityId instead of email
        // First, we need to find the user by universityId in Firestore
        // This will be handled by your server-side login endpoint

        // Server-side auth is handled in auth.controller.js which expects universityId/password
        // Return the result so the component can check for requirePasswordChange
        return await authService.loginWithUniversityId(
          credentials.universityId,
          credentials.password
        );
      } else {
        // Direct email/password login (traditional Firebase Auth)
        await authService.login(credentials.email, credentials.password);
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setCurrentUser(null);
      setUserProfile(null);
      setAuthToken(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      setLoading(true);
      setError(null);
      await authService.loginWithGoogle();
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Multi-factor Authentication (MFA) methods
  const isMFAEnabled = () => {
    return authService.isMFAEnabled();
  };

  const getMFAFactors = () => {
    return authService.getMFAFactors();
  };

  const startMFAEnrollment = async (phoneNumber) => {
    try {
      setLoading(true);
      setError(null);
      const verificationId = await authService.startMFAEnrollment(phoneNumber);
      return verificationId;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeMFAEnrollment = async (
    verificationId,
    verificationCode,
    displayName
  ) => {
    try {
      setLoading(true);
      setError(null);
      await authService.completeMFAEnrollment(
        verificationId,
        verificationCode,
        displayName
      );
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const unenrollMFAFactor = async (factorUid) => {
    try {
      setLoading(true);
      setError(null);
      await authService.unenrollMFAFactor(factorUid);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Avatar and profile management
  const uploadAvatar = async (file) => {
    try {
      setLoading(true);
      setError(null);
      const downloadURL = await authService.uploadAvatar(file);
      // Update local user profile state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          photoURL: downloadURL,
        });
      }
      return downloadURL;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      setLoading(true);
      setError(null);
      await authService.deleteAvatar();
      // Update local user profile state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          photoURL: null,
        });
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      await authService.updateUserProfile(profileData);
      // Update local user profile state
      if (userProfile) {
        setUserProfile({
          ...userProfile,
          ...profileData,
        });
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Get authentication token for API calls
  const getAuthToken = async () => {
    try {
      return await authService.getIdToken();
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  // Password reset function
  const resetPassword = async (email) => {
    try {
      setLoading(true);
      setError(null);
      await authService.resetPassword(email);
    } catch (err) {
      console.error("Password reset error:", err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    currentUser,
    userProfile,
    authToken,
    loading,
    error,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    getAuthToken,
    // MFA methods
    isMFAEnabled,
    getMFAFactors,
    startMFAEnrollment,
    completeMFAEnrollment,
    unenrollMFAFactor,
    // Avatar & profile management
    uploadAvatar,
    deleteAvatar,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
